import React from "react";
import { Link } from "react-router-dom";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    CartesianGrid,
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import StartTrialCta from "./ui/StartTrialCta";

const projectionData = buildProjectionData();

function buildProjectionData() {
    const startingNetWorth = 550_000;
    const annualContribution = 130_000;
    const expectedReturn = 0.07;
    const volatility = 0.15;
    const years = 30;

    const data = [];
    let median = startingNetWorth;
    for (let year = 0; year <= years; year++) {
        const sigmaScale = Math.sqrt(year) * volatility;
        const p10 = median * Math.exp(-1.2816 * sigmaScale);
        const p75 = median * Math.exp(0.6745 * sigmaScale);
        const p90 = median * Math.exp(1.2816 * sigmaScale);
        data.push({
            age: 29 + year,
            p50: Math.round(median),
            p75: Math.round(p75),
            band: [Math.round(p10), Math.round(p90)],
        });
        median = median * (1 + expectedReturn) + annualContribution;
    }
    return data;
}

function formatCurrency(value) {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
    return `$${value}`;
}

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0].payload;
    const [low, high] = point.band;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-sm">
            <div className="text-gray-500 text-xs mb-1">Age {label}</div>
            <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: "#1E40AF" }} />
                <span className="text-gray-900 font-semibold">{formatCurrency(point.p50)}</span>
                <span className="text-gray-500 text-xs">median</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
                <span className="h-2 w-2 rounded-full bg-success-500" />
                <span className="text-gray-900 font-semibold">{formatCurrency(point.p75)}</span>
                <span className="text-gray-500 text-xs">optimistic</span>
            </div>
            <div className="text-gray-500 text-xs mt-1">
                Range: {formatCurrency(low)} – {formatCurrency(high)}
            </div>
        </div>
    );
}

function ProjectionChart() {
    return (
        <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={projectionData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <defs>
                    <linearGradient id="bandGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1E40AF" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#1E40AF" stopOpacity={0.04} />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis
                    dataKey="age"
                    tickLine={false}
                    axisLine={{ stroke: "#E2E8F0" }}
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    label={{ value: "Age", position: "insideBottom", offset: -4, fill: "#64748B", fontSize: 12 }}
                />
                <YAxis
                    tickFormatter={formatCurrency}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    width={56}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#CBD5E1", strokeWidth: 1 }} />
                <Area
                    type="monotone"
                    dataKey="band"
                    stroke="none"
                    fill="url(#bandGradient)"
                    isAnimationActive={false}
                />
                <Line
                    type="monotone"
                    dataKey="p75"
                    stroke="#059669"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    dot={false}
                    isAnimationActive={false}
                />
                <Line
                    type="monotone"
                    dataKey="p50"
                    stroke="#1E40AF"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                />
                <ReferenceLine
                    y={3_500_000}
                    stroke="#D97706"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                        value: "FI number · $3.5M",
                        position: "insideTopRight",
                        fill: "#B45309",
                        fontSize: 11,
                        fontWeight: 500,
                    }}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
}

function TrustIcon({ children, variant = "neutral" }) {
    const variants = {
        blue: "bg-accent-50 text-accent-600 border-accent-100",
        red: "bg-danger-50 text-danger-600 border-danger-100",
        purple: "bg-purple-50 text-purple-600 border-purple-100",
        neutral: "bg-gray-100 text-gray-700 border-gray-200",
    };
    return (
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full border ${variants[variant]}`}>
            {children}
        </span>
    );
}

function FeatureIcon({ variant, children }) {
    const variants = {
        blue: "bg-accent-50 text-accent-600",
        green: "bg-success-50 text-success-600",
        amber: "bg-warning-50 text-warning-600",
        purple: "bg-purple-50 text-purple-600",
        neutral: "bg-gray-100 text-gray-700",
    };
    return (
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg mb-3 ${variants[variant]}`}>
            {children}
        </span>
    );
}

const FEATURE_ICONS = {
    netWorth: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 7-8" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 7h6v6" />
        </svg>
    ),
    fiNumber: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="5" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        </svg>
    ),
    savingsRate: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18" />
            <circle cx="8" cy="8" r="2" />
            <circle cx="16" cy="16" r="2" />
        </svg>
    ),
    monteCarlo: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M6 18V10M10 18V7M14 18V12M18 18V6" />
        </svg>
    ),
    goals: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 21V4h12l-2 4 2 4H5" />
        </svg>
    ),
    spending: (
        <svg className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v9l6 3" />
            <circle cx="12" cy="12" r="9" />
        </svg>
    ),
};

export default function LandingPage() {
    const { isAuthenticated } = useAuth();

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* Hero */}
            <section className="pt-20 pb-16 sm:pt-28 sm:pb-20 text-center">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] leading-[1.05] text-gray-900 text-balance max-w-4xl mx-auto">
                    Track your net worth. Plan your path to financial independence.
                </h1>
                <p className="mt-6 text-lg font-medium text-gray-700 max-w-2xl mx-auto leading-relaxed">
                    Simulate your financial future and plan the life you actually want.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                    <StartTrialCta plan="monthly" className="btn-primary px-7 py-3 text-base">
                        Start 14-day free trial
                    </StartTrialCta>
                    <Link to="/demo" className="btn-secondary px-7 py-3 text-base">
                        Try the demo
                    </Link>
                </div>

                {!isAuthenticated && (
                    <p className="mt-4 text-sm text-gray-500">
                        No credit card required. Cancel anytime.
                    </p>
                )}
            </section>

            {/* Product screenshot: live projection chart */}
            <section className="pb-16 sm:pb-24">
                <div className="card p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                Projection
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                Net worth through age 59
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Monte Carlo simulation · 1,000 scenarios · 10th–90th percentile band
                            </p>
                        </div>
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full" style={{ background: "#1E40AF" }} />
                                <span className="text-gray-700">Median</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="h-0.5 w-4" style={{ borderTop: "1.5px dashed #059669" }} />
                                <span className="text-gray-700">Optimistic (75th)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="h-2 w-4 rounded-sm" style={{ background: "rgba(30, 64, 175, 0.18)" }} />
                                <span className="text-gray-700">10–90% range</span>
                            </div>
                        </div>
                    </div>
                    <ProjectionChart />
                </div>
            </section>

            {/* What you get */}
            <section className="pb-16 sm:pb-24">
                <div className="max-w-3xl mb-12">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        What's inside
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 leading-tight">
                        Built for people building their future.
                    </h2>
                    <p className="mt-4 text-gray-600 leading-relaxed">
                        OmniWorth connects your banks, brokerages, and retirement accounts, then turns that data into the metrics that actually matter for financial independence.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                        {
                            title: "Net worth",
                            body: "A single number, updated daily from every connected account. Historical chart, account-by-account breakdown.",
                            variant: "blue",
                            icon: FEATURE_ICONS.netWorth,
                        },
                        {
                            title: "FI number",
                            body: "Your financial independence target calculated from your real spending, not a generic assumption.",
                            variant: "amber",
                            icon: FEATURE_ICONS.fiNumber,
                        },
                        {
                            title: "Savings rate",
                            body: "The percentage of income you keep each month, broken down by category with transfer detection.",
                            variant: "green",
                            icon: FEATURE_ICONS.savingsRate,
                        },
                        {
                            title: "Monte Carlo projections",
                            body: "1,000 simulated market paths using your actual allocation, contributions, and time horizon.",
                            variant: "blue",
                            icon: FEATURE_ICONS.monteCarlo,
                        },
                        {
                            title: "Goals",
                            body: "Set targets and track progress automatically as your balances change.",
                            variant: "purple",
                            icon: FEATURE_ICONS.goals,
                        },
                        {
                            title: "Spending breakdown",
                            body: "See where your money goes each month. Override categories when you need to.",
                            variant: "neutral",
                            icon: FEATURE_ICONS.spending,
                        },
                    ].map((item) => (
                        <div key={item.title} className="card p-6">
                            <FeatureIcon variant={item.variant}>{item.icon}</FeatureIcon>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="pb-16 sm:pb-24">
                <div className="card p-8 sm:p-12">
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center mb-10">
                        How it works
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {[
                            {
                                step: "01",
                                title: "Connect your accounts",
                                body: "Link banks, brokerages, and retirement accounts in under a minute through Plaid. Read-only. We never see your credentials.",
                            },
                            {
                                step: "02",
                                title: "See your complete picture",
                                body: "Net worth, savings rate, spending, and portfolio allocation. Calculated automatically from your real data. No manual entry.",
                            },
                            {
                                step: "03",
                                title: "Plan your path to FI",
                                body: "Set goals, run projections, and watch your progress over time. Quarterly check-ins, not daily budgeting.",
                            },
                        ].map((item) => (
                            <div key={item.step}>
                                <div className="text-sm font-semibold text-accent-500 mb-3">{item.step}</div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="pb-16 sm:pb-24">
                <div className="text-center mb-10">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Pricing
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900">
                        Simple pricing. No tiers.
                    </h2>
                    <p className="mt-3 text-gray-600">14-day free trial</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto items-stretch">
                    <div className="card p-8 flex flex-col">
                        <div className="text-sm font-medium text-gray-500 mb-1">Monthly</div>
                        <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-4xl font-semibold text-gray-900">$12</span>
                            <span className="text-gray-500">/month</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-6">Month to month</div>
                        <div className="mt-auto">
                            <StartTrialCta plan="monthly" className="btn-secondary w-full py-3 text-base">
                                Start free trial
                            </StartTrialCta>
                            <p className="mt-4 text-xs text-gray-500">Billed monthly. Cancel anytime.</p>
                        </div>
                    </div>

                    <div className="card p-8 flex flex-col ring-2 ring-accent-500 ring-offset-0 relative">
                        <div className="absolute -top-3 right-6 bg-success-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                            Save 31%
                        </div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Annual</div>
                        <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-4xl font-semibold text-gray-900">$99</span>
                            <span className="text-gray-500">/year</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-6">$8.25/mo effective</div>
                        <div className="mt-auto">
                            <StartTrialCta plan="annual" className="btn-primary w-full py-3 text-base">
                                Start free trial
                            </StartTrialCta>
                            <p className="mt-4 text-xs text-gray-500">Billed annually. Cancel anytime.</p>
                        </div>
                    </div>
                </div>

                <div className="mt-10 max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm text-gray-700">
                    {[
                        "Unlimited linked accounts",
                        "Net worth, FI number, savings rate",
                        "Monte Carlo projections",
                        "Goals with auto-tracking",
                        "Spending breakdown by category",
                        "Automatic daily data sync",
                        "Investment portfolio breakdown",
                        "Read-only Plaid access, bank-level encryption",
                    ].map((item) => (
                        <div key={item} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-accent-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Trust */}
            <section className="pb-16 sm:pb-24">
                <div className="card p-8 sm:p-12">
                    <div className="max-w-3xl">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            About
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 leading-tight">
                            Bootstrapped, independently built, built to last.
                        </h2>
                        <p className="mt-4 text-gray-600 leading-relaxed">
                            OmniWorth is a solo project. No investors, no acquisition plan, no data brokers. The incentives are simple: build the tool I want to use, charge a fair price for it, and keep it running for a long time.
                        </p>
                    </div>

                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="flex items-start gap-3">
                            <TrustIcon variant="blue">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                                </svg>
                            </TrustIcon>
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Read-only Plaid</div>
                                <div className="text-xs text-gray-500 mt-0.5">Balances and transactions only. We can't move money.</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <TrustIcon variant="blue">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <rect x="4" y="10" width="16" height="10" rx="2" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10V7a4 4 0 018 0v3" />
                                </svg>
                            </TrustIcon>
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Bank-level encryption</div>
                                <div className="text-xs text-gray-500 mt-0.5">AES-256 at rest. TLS in transit. 2FA available.</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <TrustIcon variant="red">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </TrustIcon>
                            <div>
                                <div className="text-sm font-semibold text-gray-900">We never sell your data</div>
                                <div className="text-xs text-gray-500 mt-0.5">No ads, no brokers, no third-party analytics on your financials.</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <TrustIcon variant="purple">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                                    <circle cx="12" cy="8" r="4" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                                </svg>
                            </TrustIcon>
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Solo-built, US-based</div>
                                <div className="text-xs text-gray-500 mt-0.5">Every line of code written by one person. Questions go to a human.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="pb-16 sm:pb-24">
                <div className="text-center mb-10">
                    <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900">
                        Common questions
                    </h2>
                </div>

                <div className="max-w-3xl mx-auto space-y-3">
                    {[
                        {
                            q: "Is my financial data safe?",
                            a: "Yes. We use bank-level encryption, never store your bank credentials, and only have read-only access to your accounts. We can see your balances and transactions, but can never move money.",
                        },
                        {
                            q: "What is Plaid?",
                            a: "Plaid is the secure service that connects your bank accounts to apps like OmniWorth. It's used by thousands of financial apps including Venmo, Robinhood, and most major fintech companies. When you connect an account, you're logging in through Plaid, not through us.",
                        },
                        {
                            q: "How are projections calculated?",
                            a: "We run Monte Carlo simulations using your real account data, contribution rate, and asset allocation. This models hundreds of market scenarios to give you a probability-weighted range of outcomes, not just a single optimistic guess.",
                        },
                        {
                            q: "Which banks are supported?",
                            a: "Plaid supports over 12,000 financial institutions in the US, including all major banks, credit unions, and brokerages. If you can log into your bank online, it's probably supported.",
                        },
                        {
                            q: "What is an FI number?",
                            a: "Your FI number (Financial Independence number) is how much money you need invested so that your investment returns cover all your living expenses. Once you hit this number, work becomes optional. OmniWorth calculates yours based on your actual spending and a withdrawal rate you can customize.",
                        },
                        {
                            q: "Can I cancel anytime?",
                            a: "Yes. You can cancel your subscription at any time from your Settings page. There are no cancellation fees. If you cancel, you retain access until the end of your current billing period.",
                        },
                        {
                            q: "What happens to my data if I cancel?",
                            a: "Your account and data remain intact if you cancel. You can resubscribe at any time. If you want to permanently delete your data, you can do that from the Settings page, which removes all your financial data, account connections, and history.",
                        },
                    ].map((item) => (
                        <details key={item.q} className="card p-6 group">
                            <summary className="flex justify-between items-center cursor-pointer list-none">
                                <h3 className="font-semibold text-gray-900">{item.q}</h3>
                                <svg className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </summary>
                            <p className="mt-4 text-gray-600 text-sm leading-relaxed">{item.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            {!isAuthenticated && (
                <section className="pb-24 text-center">
                    <div className="card p-10 sm:p-12 max-w-2xl mx-auto">
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                            See your real numbers.
                        </h2>
                        <p className="mt-3 text-gray-600">
                            Connect your accounts and find out exactly where you stand.
                        </p>
                        <div className="mt-8">
                            <Link to="/signup" className="btn-primary px-8 py-3 text-base">
                                Start 14-day free trial
                            </Link>
                        </div>
                        <p className="mt-4 text-sm text-gray-500">
                            No credit card required. Cancel anytime.
                        </p>
                    </div>
                </section>
            )}
        </div>
    );
}
